import { z } from 'zod';

import { countSchema, positiveIntSchema } from '@/core/schemas';

/**
 * Criteria for ranking teams on equal points, applied in the configured order.
 * Which of them count, and in what order, is a tournament decision — so it is
 * settings, not code.
 */
export const tiebreakerSchema = z.enum([
  'points',
  'wins',
  'legDifference',
  'legsFor',
  'setDifference',
  'headToHead',
]);

export type Tiebreaker = z.infer<typeof tiebreakerSchema>;

export const groupStageConfigSchema = z
  .object({
    groupCount: positiveIntSchema.max(64),
    /** How the seeded field is spread across the groups. */
    distribution: z.enum(['snake', 'sequential']),
    /** Whether every pairing is played twice. */
    doubleRound: z.boolean(),

    qualifiersPerGroup: positiveIntSchema.max(32),
    /** Extra places for the best teams on the first non-qualifying rank. */
    additionalQualifiers: countSchema.max(32),

    pointsForWin: countSchema.max(10),
    pointsForDraw: countSchema.max(10),
    pointsForLoss: countSchema.max(10),

    tiebreakers: z.array(tiebreakerSchema).min(1).max(6),
  })
  .refine((config) => config.pointsForWin >= config.pointsForDraw, {
    error: 'Ein Sieg darf nicht weniger Punkte bringen als ein Unentschieden',
    path: ['pointsForWin'],
  });

export type GroupStageConfig = z.infer<typeof groupStageConfigSchema>;

/**
 * Starting point for the settings screen. These are proposals the tournament
 * director sees and can change — the engine itself never falls back to them.
 */
export const groupStageDefaults: GroupStageConfig = {
  groupCount: 8,
  distribution: 'snake',
  doubleRound: false,
  qualifiersPerGroup: 2,
  additionalQualifiers: 0,
  pointsForWin: 3,
  pointsForDraw: 1,
  pointsForLoss: 0,
  tiebreakers: ['points', 'legDifference', 'legsFor', 'headToHead'],
};
